/* global gdAlertify */
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatDate, formatMoney } from "../utils/utils";

const CAMPOS = [
    { key: "valorTurnoPaciente", label: "Valor turno" },
    { key: "valorTurnoBasePaciente", label: "Valor base" },
    { key: "valorColacionPaciente", label: "Colacion" },
    { key: "valorLocomocionPaciente", label: "Movilizacion" },
    { key: "valorOtroPaciente", label: "Otro" },
];

const ReajusteValoresFamilia = () => {
    const [pacientes, setPacientes] = useState([]);
    const [modificaciones, setModificaciones] = useState({});
    const [busqueda, setBusqueda] = useState("");
    const [fechaReajuste, setFechaReajuste] = useState(() => new Date().toISOString().slice(0, 10));
    const [observacion, setObservacion] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [historial, setHistorial] = useState({ visible: false, paciente: null, data: [], loading: false });

    const cargarPacientes = async () => {
        setLoading(true);
        try {
            const resp = await axiosInstance.get("/paciente/reajuste-familia");
            if (resp.data?.error) throw new Error(resp.data.message || "No se pudo cargar la informacion.");
            setPacientes(resp.data.data || []);
        } catch (error) {
            gdAlertify.notiError(error?.message || "Error al cargar valores familia.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarPacientes();
    }, []);

    const pacientesFiltrados = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (!q) return pacientes;
        return pacientes.filter((p) => {
            const nombre = (p.nombre_paciente || "").toLowerCase();
            const rut = (p.rut || "").toLowerCase();
            const sector = (p.sector || "").toLowerCase();
            return nombre.includes(q) || rut.includes(q) || sector.includes(q);
        });
    }, [pacientes, busqueda]);

    const totalCambios = Object.values(modificaciones).reduce((acc, cambios) => acc + Object.keys(cambios).length, 0);
    const totalPacientes = Object.keys(modificaciones).length;

    const getValor = (row, campo) => {
        const cambio = modificaciones[row.pacienteID]?.[campo];
        return cambio ?? row[campo] ?? 0;
    };

    const setValor = (row, campo, value) => {
        setModificaciones((prev) => {
            const next = { ...prev };
            const pacienteCambios = { ...(next[row.pacienteID] || {}) };
            const nuevo = value === "" ? null : Number(value);
            const actual = Number(row[campo] || 0);

            if (nuevo === null || nuevo === actual) {
                delete pacienteCambios[campo];
            } else {
                pacienteCambios[campo] = nuevo;
            }

            if (Object.keys(pacienteCambios).length === 0) {
                delete next[row.pacienteID];
            } else {
                next[row.pacienteID] = pacienteCambios;
            }

            return next;
        });
    };

    const guardar = () => {
        if (totalPacientes === 0) {
            gdAlertify.notiError("No hay cambios para guardar.");
            return;
        }

        const datos = pacientes
            .filter((p) => modificaciones[p.pacienteID])
            .map((p) => ({
                pacienteID: p.pacienteID,
                ...modificaciones[p.pacienteID],
            }));

        gdAlertify.confirm({
            title: "Guardar reajuste valores familia",
            message: `Se guardaran ${totalCambios} cambio(s) en ${totalPacientes} paciente(s).`,
            onConfirm: async () => {
                setSaving(true);
                try {
                    const resp = await axiosInstance.post("/paciente/reajuste-familia/guardar", {
                        datos,
                        fechaReajuste,
                        observacion,
                    });
                    if (resp.data?.error) throw new Error(resp.data.message || "No se pudo guardar.");
                    gdAlertify.notiSuccess(resp.data.message || "Reajuste guardado.");
                    setModificaciones({});
                    setObservacion("");
                    cargarPacientes();
                } catch (error) {
                    gdAlertify.notiError(error.response?.data?.message || error.message || "Error al guardar reajuste.");
                } finally {
                    setSaving(false);
                }
            },
            onCancel: () => {}
        });
    };

    const abrirHistorial = async (paciente) => {
        setHistorial({ visible: true, paciente, data: [], loading: true });
        try {
            const resp = await axiosInstance.get(`/paciente/reajuste-familia/historial/${paciente.pacienteID}`);
            setHistorial({ visible: true, paciente, data: resp.data?.data || [], loading: false });
        } catch (error) {
            gdAlertify.notiError("No se pudo cargar el historial.");
            setHistorial({ visible: true, paciente, data: [], loading: false });
        }
    };

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h3 className="mb-1">Reajuste Valores Familia</h3>
                            <div className="text-muted small">Actualiza solo valores familia de pacientes activos.</div>
                        </div>
                        {totalPacientes > 0 && (
                            <span className="badge bg-warning text-dark">
                                {totalPacientes} paciente(s), {totalCambios} cambio(s)
                            </span>
                        )}
                    </div>

                    <div className="row g-3 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label">Buscar</label>
                            <input
                                className="form-control"
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                placeholder="Nombre, RUT o sector"
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Fecha reajuste</label>
                            <input
                                type="date"
                                className="form-control"
                                value={fechaReajuste}
                                onChange={(e) => setFechaReajuste(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Observacion</label>
                            <input
                                className="form-control"
                                value={observacion}
                                onChange={(e) => setObservacion(e.target.value)}
                                placeholder="Motivo o comentario opcional"
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-primary w-100 mb-0"
                                onClick={guardar}
                                disabled={saving || totalPacientes === 0}
                            >
                                {saving ? "Guardando..." : "Guardar"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mt-3">
                <div className="card-body">
                    {loading ? (
                        <div className="text-muted">Cargando pacientes...</div>
                    ) : (
                        <div className="table-responsive" style={{ maxHeight: 680 }}>
                            <table className="table table-sm table-bordered align-middle">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>Paciente</th>
                                        <th className="text-center">Sector</th>
                                        <th className="text-center">Ultimo reajuste</th>
                                        {CAMPOS.map((campo) => (
                                            <th className="text-end" key={campo.key}>{campo.label}</th>
                                        ))}
                                        <th className="text-center">Historial</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pacientesFiltrados.map((row) => {
                                        const hasChange = Boolean(modificaciones[row.pacienteID]);
                                        return (
                                            <tr key={row.pacienteID} className={hasChange ? "table-warning" : ""}>
                                                <td>
                                                    <div className="fw-bold">{row.nombre_paciente}</div>
                                                    <div className="text-muted small">{row.rut}</div>
                                                </td>
                                                <td className="text-center">{row.sector || "-"}</td>
                                                <td className="text-center">
                                                    {row.ultimo_reajuste_familia ? formatDate(row.ultimo_reajuste_familia) : "-"}
                                                </td>
                                                {CAMPOS.map((campo) => (
                                                    <td key={campo.key} style={{ minWidth: 130 }}>
                                                        <div className="input-group input-group-sm">
                                                            <span className="input-group-text">$</span>
                                                            <input
                                                                type="number"
                                                                className={`form-control text-end ${modificaciones[row.pacienteID]?.[campo.key] !== undefined ? "border-warning fw-bold" : ""}`}
                                                                value={getValor(row, campo.key)}
                                                                onChange={(e) => setValor(row, campo.key, e.target.value)}
                                                                min="0"
                                                                step="1000"
                                                            />
                                                        </div>
                                                        <div className="text-muted small text-end">
                                                            Actual: ${formatMoney(row[campo.key] || 0)}
                                                        </div>
                                                    </td>
                                                ))}
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary mb-0"
                                                        onClick={() => abrirHistorial(row)}
                                                    >
                                                        <i className="bi bi-clock-history"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {historial.visible && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.35)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Historial reajustes - {historial.paciente?.nombre_paciente}</h5>
                                <button type="button" className="btn-close" onClick={() => setHistorial({ visible: false, paciente: null, data: [], loading: false })}></button>
                            </div>
                            <div className="modal-body">
                                {historial.loading ? (
                                    <div className="text-muted">Cargando historial...</div>
                                ) : historial.data.length === 0 ? (
                                    <div className="text-muted">Sin historial registrado.</div>
                                ) : (
                                    <table className="table table-sm table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Campo</th>
                                                <th className="text-end">Anterior</th>
                                                <th className="text-end">Nuevo</th>
                                                <th>Usuario</th>
                                                <th>Observacion</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historial.data.map((row) => (
                                                <tr key={row.id}>
                                                    <td>{formatDate(row.fecha_reajuste)}</td>
                                                    <td>{CAMPOS.find(c => c.key === row.campo)?.label || row.campo}</td>
                                                    <td className="text-end">${formatMoney(row.valor_anterior)}</td>
                                                    <td className="text-end">${formatMoney(row.valor_nuevo)}</td>
                                                    <td>{row.usuario_nombre || "-"}</td>
                                                    <td>{row.observacion || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReajusteValoresFamilia;
